<!--
Copyright 2019 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <modal id="user-retire" :state="state" :hideable="!awaitingResponse" backdrop
    @hide="$emit('hide')">
    <template #title>{{ $t('title') }}</template>
    <template #body>
      <div class="modal-introduction">
        <p v-if="user != null">{{ $t('introduction[0]', user) }}</p>
        <i18n tag="p" path="introduction[1].full">
          <template #noUndo>
            <strong>{{ $t('introduction[1].noUndo') }}</strong>
          </template>
        </i18n>
        <p>{{ $t('common.areYouSure') }}</p>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-danger"
          :disabled="awaitingResponse" @click="retire">
          {{ $t('action.yesProceed') }} <spinner :state="awaitingResponse"/>
        </button>
        <button type="button" class="btn btn-link" :disabled="awaitingResponse"
          @click="$emit('hide')">
          {{ $t('action.noCancel') }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '../modal.vue';
import Spinner from '../spinner.vue';
import request from '../../mixins/request';
import { apiPaths } from '../../util/request';
import { noop } from '../../util/util';

export default {
  name: 'UserRetire',
  components: { Modal, Spinner },
  mixins: [request()],
  props: {
    state: {
      type: Boolean,
      default: false
    },
    user: Object // eslint-disable-line vue/require-default-prop
  },
  data() {
    return {
      awaitingResponse: false
    };
  },
  methods: {
    retire() {
      this.delete(apiPaths.user(this.user.id))
        .then(() => {
          this.$emit('success', this.user);
        })
        .catch(noop);
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    // This is the title at the top of a pop-up. An Administrator can use the
    // pop-up to retire another Web User.
    "title": "Retiring User",
    "introduction": [
      "You are about to retire the user account “{displayName}” \u003c{email}>. That user will be immediately barred from performing any actions and logged out.",
      {
        "full": "{noUndo}, but a new account can always be created for that person with the same email address.",
        "noUndo": "This action cannot be undone"
      }
    ]
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": "Uspaný uživatel",
    "introduction": [
      "Chystáte se uspat uživatelský účet „{displayName}“ \u003c{email}>. Tomuto uživateli bude okamžitě zakázáno provádět jakékoli akce a bude odhlášen.",
      {
        "full": "{noUndo}, ale pro tuto osobu lze vždy vytvořit nový účet se stejnou e-mailovou adresou.",
        "noUndo": "Tuto akci nelze vrátit zpět"
      }
    ]
  },
  "de": {
    "title": "User deaktivieren",
    "introduction": [
      "Sie sind dabei das Benutzerkonto \"{displayName}\" \u003c{email}> zu deaktivieren. Dieser Benutzer wird sofort am Ausführen von Aktionen gehindert und ausgeloggt.",
      {
        "full": "{noUndo}, aber es kann immer ein neues Konto mit derselben E-Mail-Adresse für diese Person angelegt werden.",
        "noUndo": "Diese Aktion kann nicht rückgängig gemacht werden."
      }
    ]
  },
  "es": {
    "title": "Retirando usuario",
    "introduction": [
      "Esta a punto de retirar el usuario \"{displayName}\"\u003c{email}> . El usuario será removido inmediatamente y se restringirá cualquier tipo de actividad en el proyecto.",
      {
        "full": "{noUndo}, sin embargo, siempre puede crear una nueva cuenta para esa persona con el mismo correo electrónico.",
        "noUndo": "Esta acción no puede ser realizada"
      }
    ]
  },
  "fr": {
    "title": "Supprimer utilisateur",
    "introduction": [
      "Vous êtes sur le point de supprimer le compte \"{displayName}\" \u003c{email}>. Cet usager sera immédiatement déconnecté et empêché d'agir sur le site.",
      {
        "full": "{noUndo}, mais un nouveau compte peut toujours être créé pour cette personne avec la même adresse de courriel.",
        "noUndo": "Cette action ne peut être annulée"
      }
    ]
  }
}
</i18n>
